import type {
    challengeSubmission,
    EventScoreWithId,
    TbEventWithId,
    TbRegistration,
} from '@moment-golf/types';
import {
    type ChallengeGroupDimension,
    type ChallengeGroupNode,
    type ChallengeGroupPathEntry,
    type EnrichedChallengeSubmission,
    getDimensionValueForReg,
    getOrderedBucketLabelsFromValues,
    UNASSIGNED_CHALLENGE_GROUP,
} from './groupChallengeSubmissions';

export type { ChallengeGroupPathEntry };

export interface ChallengePlayerOption {
    label: string;
    registration: TbRegistration;
}

const isActiveRegistration = (reg: TbRegistration): boolean =>
    !reg.canceled && !reg.withdrawn;

export function getRegistrationDimensionValue(
    reg: TbRegistration,
    dimension: ChallengeGroupDimension,
): string {
    return getDimensionValueForReg(
        {
            division: typeof reg.division === 'string' ? reg.division.trim() : '',
            flight: typeof reg.flight === 'string' ? reg.flight.trim() : '',
            gender: typeof reg.gender === 'string' ? reg.gender.trim() : '',
            age: typeof reg.age === 'number' && Number.isFinite(reg.age) ? reg.age : null,
        },
        dimension,
    );
}

export function registrationMatchesGroupPath(
    reg: TbRegistration,
    groupPath: ChallengeGroupPathEntry[],
): boolean {
    return groupPath.every(
        (entry) => getRegistrationDimensionValue(reg, entry.dimension) === entry.label,
    );
}

export function getPlayersWithChallengeSubmission(
    scores: EventScoreWithId[],
    roundNumber: number,
    challengeName: string,
    challengeHole: number,
): Set<string> {
    const names = new Set<string>();
    for (const score of scores) {
        const roundScore = score.roundScores?.find((r) => r.roundNumber === roundNumber);
        for (const submission of roundScore?.challengeSubmissions ?? []) {
            if (
                submission.challengeName === challengeName &&
                Number(submission.challengeHole) === Number(challengeHole)
            ) {
                const name = submission.playerName?.trim();
                if (name) names.add(name);
            }
        }
    }
    return names;
}

export function getPlayersWithoutChallengeSubmission(
    registrations: TbRegistration[],
    scores: EventScoreWithId[],
    roundNumber: number,
    challengeName: string,
    challengeHole: number,
    groupPath?: ChallengeGroupPathEntry[],
): ChallengePlayerOption[] {
    const submitted = getPlayersWithChallengeSubmission(
        scores,
        roundNumber,
        challengeName,
        challengeHole,
    );

    return registrations
        .filter(isActiveRegistration)
        .filter((reg) => {
            const name = reg.playerName?.trim();
            if (!name || submitted.has(name)) return false;
            if (groupPath?.length && !registrationMatchesGroupPath(reg, groupPath)) {
                return false;
            }
            return true;
        })
        .map((registration) => ({
            label: registration.playerName!.trim(),
            registration,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
}

export function resolvePlayerEventScore(
    playerName: string,
    registrations: TbRegistration[],
    scores: EventScoreWithId[],
    roundNumber: number,
): { score: EventScoreWithId; roundIndex: number; eventScoreID: string } | null {
    const name = playerName.trim();
    const reg = registrations.find((r) => r.playerName?.trim() === name);
    if (!reg?.eventScoreID) return null;

    const score = scores.find((s) => String(s._id) === String(reg.eventScoreID));
    if (!score) return null;

    const roundIndex = score.roundScores.findIndex((r) => r.roundNumber === roundNumber);
    if (roundIndex < 0) return null;

    return { score, roundIndex, eventScoreID: String(score._id) };
}

export function upsertChallengeSubmissionOnScore(
    score: EventScoreWithId,
    roundIndex: number,
    submission: challengeSubmission,
): challengeSubmission[] {
    const existing = [...(score.roundScores[roundIndex].challengeSubmissions ?? [])];
    const matchIdx = existing.findIndex(
        (s) =>
            s.challengeName === submission.challengeName &&
            Number(s.challengeHole) === Number(submission.challengeHole) &&
            s.playerName === submission.playerName,
    );
    if (matchIdx >= 0) {
        existing[matchIdx] = submission;
    } else {
        existing.push(submission);
    }
    score.roundScores[roundIndex].challengeSubmissions = existing;
    return existing;
}

export function removeChallengeSubmissionFromScore(
    score: EventScoreWithId,
    roundIndex: number,
    target: {
        challengeName: string;
        challengeHole: number;
        playerName: string;
        value?: unknown;
        submittedAt?: string | Date | null;
    },
): challengeSubmission[] {
    const existing = [...(score.roundScores[roundIndex].challengeSubmissions ?? [])];
    let removed = false;
    const next = existing.filter((s) => {
        if (removed) return true;
        const nameMatch =
            s.challengeName === target.challengeName &&
            Number(s.challengeHole) === Number(target.challengeHole) &&
            s.playerName === target.playerName;
        if (!nameMatch) return true;

        if (target.submittedAt != null && s.submittedAt != null) {
            const a = new Date(s.submittedAt).getTime();
            const b = new Date(target.submittedAt).getTime();
            if (Number.isFinite(a) && Number.isFinite(b) && a === b) {
                if (target.value === undefined || Object.is(s.value, target.value)) {
                    removed = true;
                    return false;
                }
            }
            return true;
        }

        if (target.value !== undefined && !Object.is(s.value, target.value)) {
            return true;
        }

        removed = true;
        return false;
    });

    // Fall back: remove first name match if no precise match found
    if (!removed) {
        const idx = existing.findIndex(
            (s) =>
                s.challengeName === target.challengeName &&
                Number(s.challengeHole) === Number(target.challengeHole) &&
                s.playerName === target.playerName,
        );
        if (idx >= 0) {
            existing.splice(idx, 1);
            score.roundScores[roundIndex].challengeSubmissions = existing;
            return existing;
        }
    }

    score.roundScores[roundIndex].challengeSubmissions = next;
    return next;
}

function buildRegistrationGroupTree(
    registrations: TbRegistration[],
    submissions: EnrichedChallengeSubmission[],
    dimensions: ChallengeGroupDimension[],
    dimensionIndex: number,
    event: TbEventWithId | null | undefined,
    path: ChallengeGroupPathEntry[],
): ChallengeGroupNode[] {
    if (dimensionIndex >= dimensions.length) return [];

    const dimension = dimensions[dimensionIndex];
    const values = registrations.map((reg) => getRegistrationDimensionValue(reg, dimension));
    const bucketLabels = getOrderedBucketLabelsFromValues(dimension, values, event);
    const isLeaf = dimensionIndex === dimensions.length - 1;
    const nodes: ChallengeGroupNode[] = [];

    for (const label of bucketLabels) {
        const bucketRegs = registrations.filter(
            (reg) => getRegistrationDimensionValue(reg, dimension) === label,
        );
        if (bucketRegs.length === 0) continue;

        const nextPath = [...path, { dimension, label }];

        if (isLeaf) {
            const leafSubs = submissions.filter((s) =>
                nextPath.every((entry) => {
                    const enriched = {
                        division: s.division,
                        flight: s.flight,
                        gender: s.gender,
                        age: s.age,
                    };
                    return getDimensionValueForReg(enriched, entry.dimension) === entry.label;
                }),
            );
            nodes.push({
                label,
                submissions: leafSubs,
                groupPath: nextPath,
            });
        } else {
            nodes.push({
                label,
                children: buildRegistrationGroupTree(
                    bucketRegs,
                    submissions,
                    dimensions,
                    dimensionIndex + 1,
                    event,
                    nextPath,
                ),
                groupPath: nextPath,
            });
        }
    }

    return nodes;
}

/**
 * Build challenge group tree from active registrations so empty buckets still appear,
 * then attach matching submissions to leaf nodes.
 */
export function buildChallengeGroupTreeFromRegistrations(
    registrations: TbRegistration[],
    submissions: EnrichedChallengeSubmission[],
    enabled: ChallengeGroupDimension[],
    event: TbEventWithId | null | undefined,
): ChallengeGroupNode[] {
    if (enabled.length === 0) return [];
    const active = registrations.filter(isActiveRegistration);
    if (active.length === 0) return [];
    return buildRegistrationGroupTree(active, submissions, enabled, 0, event, []);
}

export { UNASSIGNED_CHALLENGE_GROUP };
